import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupervisorEvent } from '@/types/supervisor';

export type MissingField = 'size' | 'paper' | 'printer';

type SelectionHelpKind = 'size' | 'paper' | 'printer';
type SelectionHelpReason = 'noItems' | 'noSelection';

type SupervisorSender = (event: SupervisorEvent) => void;

type FallbackMap = Record<SelectionHelpKind, Record<SelectionHelpReason, string>>;

const buildFallbackMessage = (
  t: ReturnType<typeof useTranslation>['t'],
  kind: SelectionHelpKind,
  reason: SelectionHelpReason,
): string => {
  const defaults: FallbackMap = {
    size: {
      noItems: 'Please upload at least one image before adjusting print size.',
      noSelection: 'Select at least one image to adjust print size.',
    },
    paper: {
      noItems: 'Please upload at least one image before choosing paper.',
      noSelection: 'Select at least one image to choose paper.',
    },
    printer: {
      noItems: 'Please upload at least one image before choosing a printer.',
      noSelection: 'Select at least one image to choose a printer.',
    },
  };

  return t(`uploadedImages.aiFallback.${kind}.${reason}`, defaults[kind][reason]);
};

const buildInstruction = (kind: SelectionHelpKind, reason: SelectionHelpReason): string => {
  if (kind === 'size') {
    return reason === 'noItems'
      ? 'Politely explain that the user needs to upload at least one image before adjusting print size.'
      : 'Politely explain that the user needs to select at least one uploaded image before adjusting print size.';
  }

  if (kind === 'paper') {
    return reason === 'noItems'
      ? 'Politely explain that the user needs to upload at least one image before choosing a paper type for printing.'
      : 'Politely explain that the user needs to select at least one uploaded image before choosing a paper type.';
  }

  return reason === 'noItems'
    ? 'Politely explain that the user needs to upload at least one image before choosing a printer.'
    : 'Politely explain that the user needs to select at least one uploaded image before choosing a printer.';
};

const buildMissingFieldLabel = (t: ReturnType<typeof useTranslation>['t'], field: MissingField): string => {
  switch (field) {
    case 'size':
      return t('uploadedImages.missingFields.size', 'Print size');
    case 'paper':
      return t('uploadedImages.missingFields.paper', 'Paper');
    case 'printer':
      return t('uploadedImages.missingFields.printer', 'Printer');
    default:
      return field;
  }
};

const singleFieldFallbackDefaults: Record<MissingField, string> = {
  size: 'Please set a print size for every selected image before adding them to the order.',
  paper: 'Please choose a paper type for every selected image before adding them to the order.',
  printer: 'Please choose a printer for every selected image before adding them to the order.',
};

export const useOrderAiPrompts = (sendToSupervisor: SupervisorSender) => {
  const { t } = useTranslation();

  const sendSelectionHelpPrompt = useCallback((kind: SelectionHelpKind, reason: SelectionHelpReason) => {
    const aiPrompt = buildInstruction(kind, reason);
    const fallbackMessage = buildFallbackMessage(t, kind, reason);

    sendToSupervisor({
      type: 'UI.REQUEST_AI_PROMPT',
      aiPrompt,
      fallbackMessage,
    });
  }, [sendToSupervisor, t]);

  const sendMissingConfigurationPrompt = useCallback((missing: MissingField[]) => {
    if (missing.length === 0) {
      return;
    }

    const uniqueMissing = Array.from(new Set(missing));

    if (uniqueMissing.length === 1) {
      const [single] = uniqueMissing;

      const fallbackMessage = t(
        `uploadedImages.aiFallback.single.${single}`,
        singleFieldFallbackDefaults[single],
      );

      const aiPrompt = (() => {
        switch (single) {
          case 'size':
            return 'Politely explain that the user needs to set a print size for every selected image before adding them to the order.';
          case 'paper':
            return 'Politely explain that the user needs to choose a paper type for every selected image before adding them to the order.';
          case 'printer':
            return 'Politely explain that the user needs to choose a printer for every selected image before adding them to the order.';
          default:
            return `Politely explain that the user needs to configure ${single} for every selected image before adding them to the order.`;
        }
      })();

      sendToSupervisor({
        type: 'UI.REQUEST_AI_PROMPT',
        aiPrompt,
        fallbackMessage,
      });
      return;
    }

    const labels = uniqueMissing.map(field => buildMissingFieldLabel(t, field));
    const fieldsList = labels.join(', ');

    const fallbackMessage = t(
      'uploadedImages.aiFallback.missingConfiguration',
      'Please configure: {{fields}} for your selected images before adding them to the order.',
      { fields: fieldsList },
    );

    const aiPrompt = `Politely explain that the user needs to configure the following before adding their image to the order: ${fieldsList}.`;

    sendToSupervisor({
      type: 'UI.REQUEST_AI_PROMPT',
      aiPrompt,
      fallbackMessage,
    });
  }, [sendToSupervisor, t]);

  return { sendSelectionHelpPrompt, sendMissingConfigurationPrompt };
};

export const useSelectionHelpPrompt = (sendToSupervisor: SupervisorSender) => {
  return useOrderAiPrompts(sendToSupervisor).sendSelectionHelpPrompt;
};
